import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { PersonModel, ContactDetailsModel, AddressModel, CompanyModel, EmailModel, PhoneNumberModel, PhoneNumberSettingsModel, EmailSettingsModel } from '../../common/models/CommonModels';
import { DetailsValueBlock, DetailsValueBlockWithButtons } from './DetailsValueBlock';
import { localizations } from '../../localization/localization';
import { RegisteredPersonModel } from '../../admin/models/CompanyModels';
import Delete from '@material-ui/icons/Delete'
import Edit from '@material-ui/icons/Edit'
import { momentToString } from '../../utils/dateUtilities';
import Visibility from '../Visibility';
import CompanyActivityMark from "../../common/companyActivityMark";
import { TruckProperties } from '../../trucks/TruckModels';

export const renderPersonDetails = (person: PersonModel | undefined, title: string) => {
    return person ?
        <DetailsValueBlock title={title}>
            <Typography>{person.firstName} {person.lastName}</Typography>
            {person.contact ? <Typography><a href={"mailto: " + person.contact.email}>{person.contact.email}</a></Typography> : null}
            {person.contact && person.contact.emailDetails ? <Typography><i>{person.contact.emailDetails}</i></Typography> : null}
            {person.contact ? <Typography>{person.contact.phoneNumber}</Typography> : null}
            {person.contact && person.contact.phoneDetails ? <Typography><i>{person.contact.phoneDetails}</i></Typography> : null}
        </DetailsValueBlock>
        : null;
}

export const renderContactPersonDetails = (person: PersonModel | undefined) => {
    return renderPersonDetails(person, localizations.common.contactPerson);
}

export const renderEmployee = (person: RegisteredPersonModel | undefined, deleteUser: (person: RegisteredPersonModel) => void) => {

    const actions = <Grid container direction="row">
        <IconButton style={{ height: 32, width: 32 }} onClick={() => person && deleteUser(person)}><Delete color="primary" /></IconButton>
    </Grid>

    return person ?
        <DetailsValueBlockWithButtons title={`${person.firstName} ${person.lastName}`} actions={actions}>
            {person.contact ? <Typography><a href={"mailto: " + person.contact.email}>{person.contact.email}</a></Typography> : null}
            {person.contact ? <Typography>{person.contact.phoneNumber}</Typography> : null}
            <Typography>{momentToString(person.registeredAt)}</Typography>
        </DetailsValueBlockWithButtons>
        : null;
}

export const renderEmailDetails = (contact: ContactDetailsModel | EmailModel | undefined, index?: number) => {

    const title = localizations.common.email + (index ? ` ${index}` : "");

    return contact ?
        <DetailsValueBlock title={title}>
            <Typography><a href={"mailto: " + contact.email}>{contact.email}</a></Typography>
            <Typography><i>{contact.emailDetails}</i></Typography>
        </DetailsValueBlock>
        : null;
}

export const renderAddressDetails = (address: AddressModel | undefined) => {
    return address ?
        <DetailsValueBlock title={localizations.common.streetAddress}>
            <Typography>{address.streetAddress}</Typography>
            <Typography>{address.postalCode} {address.city}</Typography>
        </DetailsValueBlock>
        : null;
}

export const renderPhoneDetails = (contact: ContactDetailsModel | PhoneNumberModel | undefined, index?: number) => {

    const title = localizations.common.phoneNumber + (index ? ` ${index}` : "");

    return contact ?
        <DetailsValueBlock title={title}>
            <Typography>{contact.phoneNumber}</Typography>
            {contact.phoneDetails ? <Typography><i>{contact.phoneDetails}</i></Typography> : null}
        </DetailsValueBlock>
        : null;
}

export const renderPhoneDetailsWithEdit = (contact: PhoneNumberSettingsModel | undefined, onEdit: OnEditHandler<PhoneNumberSettingsModel>, onDelete?: OnDeleteHandler<PhoneNumberSettingsModel>, index?: number) => {

    const title = localizations.common.phoneNumber + (index ? ` ${index}` : "");
    const isVisible = !!contact && contact.phoneVisible;

    const actions = <Grid container direction="row">
        {/* <VisibilityControls isVisible={isVisible} /> */}
        <IconButton aria-label={localizations.actions.edit} style={{ height: 24, width: 24 }} onClick={() => contact && onEdit(contact)}><Edit color="primary" /></IconButton>
        {onDelete ? <IconButton aria-label={localizations.actions.delete} style={{ height: 24, width: 24 }} onClick={() => contact && onDelete(contact)}><Delete color="error" /></IconButton> : null}
    </Grid>

    return contact ?
        <DetailsValueBlockWithButtons title={title} actions={actions}>
            <Typography>{contact.phoneNumber}</Typography>
            {contact.phoneDetails ? <Typography><i>{contact.phoneDetails}</i></Typography> : null}
            <Grid item><Grid container direction="row" spacing={8} alignItems="center"><Grid item><Visibility isVisible={isVisible} /></Grid><Grid item><Typography>{isVisible ? localizations.actions.visible : localizations.actions.notVisible}</Typography></Grid></Grid></Grid>
        </DetailsValueBlockWithButtons>
        : null;
}

type OnEditHandler<T> = (data: T) => void;
type OnDeleteHandler<T> = (data: T) => void;

export const renderEmailDetailsWithEdit = (contact: EmailSettingsModel | undefined, onEdit: OnEditHandler<EmailSettingsModel>, onDelete?: OnDeleteHandler<EmailSettingsModel>, index?: number) => {

    const title = localizations.common.email + (index ? ` ${index}` : "");
    const isVisible = !!contact && contact.emailVisible;

    const actions = <Grid container direction="row">
        <IconButton aria-label={localizations.actions.edit} style={{ height: 24, width: 24 }} onClick={() => contact && onEdit(contact)}><Edit color="primary" /></IconButton>
        {onDelete ? <IconButton aria-label={localizations.actions.delete} style={{ height: 24, width: 24 }} onClick={() => contact && onDelete(contact)}><Delete color="error" /></IconButton> : null}
    </Grid>

    return contact ?
        <DetailsValueBlockWithButtons title={title} actions={actions}>
            <Typography><a href={"mailto: " + contact.email}>{contact.email}</a></Typography>
            <Typography><i>{contact.emailDetails}</i></Typography>
            <Grid item><Grid container direction="row" spacing={8} alignItems="center"><Grid item><Visibility isVisible={isVisible} /></Grid><Grid item><Typography>{isVisible ? localizations.actions.visible : localizations.actions.notVisible}</Typography></Grid></Grid></Grid>
        </DetailsValueBlockWithButtons>
        : null;
}

export const renderCompanyDetails = (company: CompanyModel | undefined) => {
    return company ?
        <DetailsValueBlock title={localizations.common.companyName}>
            <Typography>{company.name}</Typography>
            <Typography><i>{company.businessId}</i></Typography>
        </DetailsValueBlock>
        : null;
}

export const renderWebsiteDetails = (website: string | undefined) => {

    let url = website;

    if (website) {
        if (!website.includes("http")) {
            url = "http://" + url;
        }
    }

    return website ?
        <DetailsValueBlock title={localizations.common.website}>
            <Typography><a target="_blank" href={`${url}`}>{website}</a></Typography>
        </DetailsValueBlock>
        : null;
}

export const renderDescriptionDetails = (description: string | undefined ) => {
    return (description ? 
        <DetailsValueBlock title={localizations.company.companyDescription}>
            <Typography>{description}</Typography>
        </DetailsValueBlock>
        : null);
}

export const renderIndustryTypeDetails = (industryType: string | undefined ) => {
    return (industryType ? 
        <DetailsValueBlock title={localizations.company.companyIndustryType}>
            <Typography>{localizations.companyIndustryTypes[industryType]}</Typography>
        </DetailsValueBlock>
        : null);
}

export const renderAnnouncementCountDetails = (announcementCount: number) => {

    const title = localizations.common.announcementCountTitle;

    return (
        <DetailsValueBlock title={title}>
            <Typography>{localizations.common.announcementCount} {announcementCount}</Typography>
            <CompanyActivityMark isCompany={false} level={announcementCount} />
        </DetailsValueBlock>
        );
}

export const renderTruckProperties = (properties: TruckProperties) => {
    return <Grid container direction="column">
        {properties.adr ? <Grid item><Typography>{localizations.trucks.properties.adr}</Typography></Grid> : null}
        {properties.thermal ? <Grid item><Typography>{localizations.trucks.properties.thermal}</Typography></Grid> : null}
        {properties.glass ? <Grid item><Typography>{localizations.trucks.properties.glass}</Typography></Grid> : null}
        {properties.tank ? <Grid item><Typography>{localizations.trucks.properties.tank}</Typography></Grid> : null}
        {properties.mega ? <Grid item><Typography>{localizations.trucks.properties.mega}</Typography></Grid> : null}
        {properties.explosive ? <Grid item><Typography>{localizations.trucks.properties.explosive}</Typography></Grid> : null}
        {properties.container20 ? <Grid item><Typography>{localizations.trucks.properties.container20}</Typography></Grid> : null}
        {properties.containerOver20 ? <Grid item><Typography>{localizations.trucks.properties.containerOver20}</Typography></Grid> : null}
        {properties.pieceGoods ? <Grid item><Typography>{localizations.trucks.properties.pieceGoods}</Typography></Grid> : null}
        {properties.bulkGoods ? <Grid item><Typography>{localizations.trucks.properties.bulkGoods}</Typography></Grid> : null}
        {properties.trailer ? <Grid item><Typography>{localizations.units.trailer}</Typography></Grid> : null}
    </Grid>
}


// WEBPACK FOOTER //
// ./src/components/tables/DetailsHelpers.tsx